import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationAutoClose,
  moleculeNotificationBorderSizes,
  moleculeNotificationPosition,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import {useLocation, useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import {PanelBasic} from '@adv-mt/mt-panel-basic'
import IconCircleCheck from '@adv-mt/mt-svg-icons/lib/CircleCheck'
import IconForbidden from '@adv-mt/mt-svg-icons/lib/Forbidden'
import TitleBasic, {titleBasicSizes} from '@adv-mt/mt-title-basic'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import FormUserAvatar from './components/FormUserAvatar/index.js'
import FormUserModifyPassword from './components/FormUserModifyPassword/index.js'
import FormUserPersonalData from './components/FormUserPersonalData/index.js'
import {getInitialProps} from './getInitialProps.js'

const BASE_CLASS = 'mt-userProfileEdit'

const MOLECULE_NOTIFICATION_PROPS = {
  alignItems: moleculeNotificationAlignItems.CENTER,
  autoClose: moleculeNotificationAutoClose.manual,
  effect: false,
  position: moleculeNotificationPosition.relative,
  roundedCorners: moleculeNotificationBorderSizes.medium,
  showLeftIconMobile: true,
  variation: moleculeNotificationVariations.positive
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  })
}

const UserProfileEdit = ({helmet, pageName, userData}) => {
  const {domain, i18n} = useContext(Context)

  const router = useRouter()
  const {pathname, query, search} = useLocation()

  const [showModifyPasswordForm, setShowModifyPasswordForm] = useState(false)
  const [notification, setNotification] = useState()

  const config = domain.get('config')
  const {phoneVerificationStatus, returnUrl} = config.get('COMMON').QUERY_PARAMS.USER

  useMount(() => {
    if (phoneVerificationStatus in query) {
      const message = {
        error: 'USER_PHONE_VERIFICATION.ERROR_NOTIFICATION',
        success: 'USER_PHONE_VERIFICATION.SUCCESS_NOTIFICATION'
      }
      const icon = {
        error: <IconForbidden size={ATOM_ICON_SIZES.medium} />,
        success: <IconCircleCheck size={ATOM_ICON_SIZES.medium} />
      }
      setNotification({
        message: message[query[phoneVerificationStatus]],
        icon: icon[query[phoneVerificationStatus]],
        type: moleculeNotificationTypes[query[phoneVerificationStatus]]
      })
    }
  })

  const handleClickModifyPassword = () => {
    scrollToTop()
    setShowModifyPasswordForm(true)
  }

  const handleClickGoBack = () => {
    scrollToTop()
    setShowModifyPasswordForm(false)
  }

  const handleErrorUpdatingUser = ({error}) => {
    const {UPDATE_PHONE_USER_ERROR} = config.get('ERRORS')
    const isPhoneUserError = error instanceof UPDATE_PHONE_USER_ERROR

    scrollToTop()
    setNotification({
      message: isPhoneUserError
        ? 'USER_PROFILE.EDIT.ERROR_PHONE_DUPLICATED_NOTIFICATION'
        : 'USER_PROFILE.EDIT.ERROR_NOTIFICATION',
      icon: <IconForbidden size={ATOM_ICON_SIZES.medium} />,
      type: moleculeNotificationTypes.error
    })
  }

  const handleSuccessUpdatingUser = () => {
    scrollToTop()
    setNotification({
      message: 'USER_PROFILE.EDIT.SUCCESS_NOTIFICATION',
      icon: <IconCircleCheck size={ATOM_ICON_SIZES.medium} />,
      type: moleculeNotificationTypes.success
    })
    if (returnUrl in query) router.push(decodeURIComponent(query[returnUrl]))
  }

  return (
    <NavigationRequireAuth currentPath={`${pathname}${search}`}>
      <Layout.Basic isPaddingless linkFactory={LinkFactory}>
        <Helmet {...helmet} />
        <PanelBasic>
          <MoleculeNotification
            {...MOLECULE_NOTIFICATION_PROPS}
            icon={notification?.icon}
            onClose={() => setNotification(null)}
            show={Boolean(notification)}
            type={notification?.type}
          >
            {i18n.t(notification?.message)}
          </MoleculeNotification>
          <div className={BASE_CLASS}>
            <div className={`${BASE_CLASS}-title`}>
              <TitleBasic size={titleBasicSizes.m} title={i18n.t('USER_PROFILE.EDIT.TITLE')} />
            </div>
            {showModifyPasswordForm ? (
              <FormUserModifyPassword onClickGoBack={() => handleClickGoBack()} />
            ) : (
              <>
                <FormUserAvatar userData={userData} />
                <FormUserPersonalData
                  onClickModifyPassword={handleClickModifyPassword}
                  onErrorUpdatingUser={handleErrorUpdatingUser}
                  onSuccessUpdatingUser={handleSuccessUpdatingUser}
                  userData={userData}
                />
              </>
            )}
          </div>
        </PanelBasic>
        <TrackingPageView pageName={pageName} />
      </Layout.Basic>
    </NavigationRequireAuth>
  )
}

UserProfileEdit.displayName = 'UserProfileEdit'

UserProfileEdit.getInitialProps = getInitialProps

UserProfileEdit.propTypes = {
  helmet: PropTypes.object,
  pageName: PropTypes.string,
  userData: PropTypes.shape({
    avatarUrl: PropTypes.string,
    email: PropTypes.string,
    hidePhone: PropTypes.bool,
    lastName: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    zipCode: PropTypes.string
  })
}

export default UserProfileEdit
